import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialog } from '../../../../common/interfaces/confirm-dialog.interface';
import {
    GenericStreamListForUserUse,
    GenericUserStreamWithProgress,
} from '../../../../interfaces/responsedata.ts/generic-streams';
import { MentorConnectedUser } from '../../../../interfaces/responsedata.ts/mentor';
import { DialogService } from '../../../../service/dialog.service';
import { GenericStreamService } from '../../../../service/generic-stream.service';
import { MentorService } from '../../../../service/mentor.service';

@Component({
    selector: 'app-active-founder-details',
    standalone: false,
    templateUrl: './active-founder-details.component.html',
    styleUrl: './active-founder-details.component.scss',
})
export class ActiveFounderDetailsComponent implements OnInit {
    public readonly data = inject<{ activeFounder: MentorConnectedUser }>(
        MAT_DIALOG_DATA,
    );

    protected finishedStreams: GenericUserStreamWithProgress[] = [];
    protected streamsData: GenericStreamListForUserUse[] = [];
    protected isLoading = false;

    public showUserGateContent = false;
    private destroyRef = inject(DestroyRef);
    private dialogService = inject(DialogService);
    private dialogRef = inject(MatDialogRef<ActiveFounderDetailsComponent>);
    private genericStreamService = inject(GenericStreamService);
    protected mentorService = inject(MentorService);

    public async ngOnInit(): Promise<void> {
        if (!this.data.activeFounder.id) {
            return;
        }

        const finishedStreams =
            await this.genericStreamService.getFinishedUserStreams(
                this.data.activeFounder.id,
            );

        if (typeof finishedStreams === 'boolean') {
            return;
        }

        this.finishedStreams = finishedStreams;

        let configIds = this.finishedStreams.map((stream) => stream.configId);

        // removes duplicates
        configIds = Array.from(new Set(configIds));

        const streamsData =
            await this.genericStreamService.getGenericUserStreamsByIds(
                this.data.activeFounder.id,
                configIds,
            );
        if (typeof streamsData === 'boolean') {
            return;
        }

        for (const finishedStream of this.finishedStreams) {
            const streamData = streamsData.find(
                (stream) => stream.id === finishedStream.configId,
            );

            if (streamData) {
                this.streamsData.push(streamData);
            }
        }
    }
    public checkIfUserWaitsOnGate(): { waiting: boolean, index: number | undefined} {
        if (!this.data.activeFounder.userGateProgress?.length) return { waiting: false, index: -1 };

        for (let i = 0; i < this.data.activeFounder.userGateProgress?.length; i++) {
            if (
                this.data.activeFounder.userGateProgress[i].closed === false &&
                this.data.activeFounder.userGateProgress[i].confirmedByCoach === false &&
                this.data.activeFounder.userGateProgress[i-1].closed === true &&
                this.data.activeFounder.userGateProgress[i-1].confirmedByCoach === true
            ) {
                return { waiting: true, index: i};
            }
        }

        return { waiting: false, index: undefined };
    }

    public async activateGate(index: number): Promise<void> {
        if (!this.data.activeFounder.userGateProgress || !this.data.activeFounder.userGateProgress[index]) return;

        const result = await this.mentorService.activateUserGate(this.data.activeFounder.id, index);

        if (result === true) {
            this.data.activeFounder.userGateProgress[index].confirmedByCoach = true;
        }

    }

    protected revokeAccess(): void {
        const dialogData: ConfirmDialog = {
            headerText: 'Revoke access?',
            dialogText:
                'Are you sure that you want to revoke the access to this founder? By doing so, you will be granted a new seat on your current licence.',
            submitText: 'Yes, revoke',
            cancelText: 'Cancel',
        };

        this.dialogService
            .openConfirmationDialog$(dialogData)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (revokeSuccess) => {
                    if (!revokeSuccess) {
                        return;
                    }

                    this.dialogRef.close({ revoked: true });
                },
            });
    }

    protected close(): void {
        this.dialogRef.close();
    }

    protected fieldLabel(id: string): string {
        return id.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
    }
}
